import { create } from "zustand";
import { persist } from "zustand/middleware";
import { getPresetThemeStyles, getPresetByName } from "../utils/theme-preset-helper";
import { applyThemeToDocument, getChartColors as getPresetChartColors } from "../utils/theme-style-generator";
import { CHART_PALETTES, getChartPaletteColors, DEFAULT_CUSTOM_CHART_COLORS, type ChartPaletteId } from "../utils/chart-palettes";

interface ThemeState {
  preset: string;
  currentMode: "light" | "dark";
  styles: Record<string, Record<string, string>>;
  customStyles: Record<string, string>;
  /** Chart-only palette: 'theme' = use preset colors; 'custom' = user-saved; else fixed palette. */
  chartPaletteId: ChartPaletteId;
  chartColors: string[];
  /** User-saved custom chart colors (used when chartPaletteId === 'custom'). Persisted. */
  customChartColors: string[];
}

interface EditorStore {
  themeState: ThemeState;
  hasUnsavedChanges: () => boolean;
  applyThemePreset: (presetName: string) => void;
  setThemeMode: (mode: "light" | "dark") => void;
  setChartPalette: (paletteId: ChartPaletteId) => void;
  setCustomChartColors: (colors: string[]) => void;
  updateCustomStyles: (styles: Record<string, string>) => void;
  resetCustomStyles: () => void;
  getThemeStyles: () => Record<string, string>;
  getChartColors: () => string[];
  hydrateThemeState: () => void;
}

const defaultChartColors = CHART_PALETTES.brand;

// Default theme is the Smart Insights theme used by the finance workspace.
// If it doesn't exist, fallback to "modern-minimal".
const defaultPresetName = "smart-insights";
const fallbackPresetName = "modern-minimal";
const initialPreset = getPresetByName(defaultPresetName) ? defaultPresetName : fallbackPresetName;
const initialThemeStyles = getPresetThemeStyles(initialPreset);

const initialState: ThemeState = {
  preset: initialPreset,
  currentMode: "light",
  styles: {
    light: initialThemeStyles["light"],
    dark: initialThemeStyles["dark"]
  },
  customStyles: {},
  chartPaletteId: "brand",
  chartColors: defaultChartColors,
  customChartColors: DEFAULT_CUSTOM_CHART_COLORS,
};

export const useEditorStore = create<EditorStore>()(
  persist(
    (set, get) => ({
      themeState: { ...initialState },

      // Ensure styles are always available when store is hydrated
      hydrateThemeState: () => {
        const currentState = get().themeState;
        if (!currentState.styles || !currentState.styles[currentState.currentMode]) {
          const preset = getPresetByName(currentState.preset);
          if (preset) {
            const themeStyles = getPresetThemeStyles(currentState.preset);
            const chartColors =
              currentState.chartPaletteId === "theme"
                ? getPresetChartColors(preset, currentState.currentMode)
                : currentState.chartPaletteId === "custom"
                  ? (currentState.customChartColors?.length ? currentState.customChartColors : DEFAULT_CUSTOM_CHART_COLORS)
                  : (getChartPaletteColors(currentState.chartPaletteId) ?? currentState.chartColors);
            set((state) => ({
              themeState: {
                ...state.themeState,
                styles: themeStyles,
                chartColors,
              },
            }));
          }
        }
      },

      hasUnsavedChanges: () => {
        const { customStyles } = get().themeState;
        return Object.keys(customStyles).length > 0;
      },

      applyThemePreset: (presetName: string) => {
        console.log('🎨 Theme Preset Debug - Applying preset:', presetName);
        const currentMode = get().themeState.currentMode;
        const preset = getPresetByName(presetName);

        if (!preset) {
          console.warn('🎨 Theme Preset Debug - Preset not found:', presetName);
          return;
        }

        const themeStyles = getPresetThemeStyles(presetName);
        const { chartPaletteId } = get().themeState;
        const chartColors =
          chartPaletteId === "theme"
            ? getPresetChartColors(preset, currentMode)
            : get().themeState.chartColors;

        console.log('🎨 Theme Preset Debug - Theme styles for mode:', currentMode, themeStyles[currentMode]);

        set((state) => ({
          themeState: {
            ...state.themeState,
            preset: presetName,
            styles: themeStyles,
            chartColors,
            customStyles: {}, // Reset custom styles when applying preset
          },
        }));

        // Apply theme to document
        applyThemeToDocument(preset, currentMode, {});
      },

      setThemeMode: (mode: "light" | "dark") => {
        const { preset, customStyles, chartPaletteId } = get().themeState;
        const themePreset = getPresetByName(preset);

        if (!themePreset) return;

        const chartColors =
          chartPaletteId === "theme"
            ? getPresetChartColors(themePreset, mode)
            : get().themeState.chartColors;

        set((state) => ({
          themeState: {
            ...state.themeState,
            currentMode: mode,
            chartColors,
          },
        }));

        // Apply theme to document
        applyThemeToDocument(themePreset, mode, customStyles);
      },

      setChartPalette: (paletteId: ChartPaletteId) => {
        const { customChartColors, preset, currentMode } = get().themeState;
        const themePreset = getPresetByName(preset);
        const chartColors =
          paletteId === "theme" && themePreset
            ? getPresetChartColors(themePreset, currentMode)
            : paletteId === "custom"
              ? (customChartColors?.length ? customChartColors : DEFAULT_CUSTOM_CHART_COLORS)
              : (getChartPaletteColors(paletteId) ?? get().themeState.chartColors);
        set((state) => ({
          themeState: {
            ...state.themeState,
            chartPaletteId: paletteId,
            chartColors,
          },
        }));
      },

      setCustomChartColors: (colors: string[]) => {
        const safe = Array.isArray(colors) && colors.length > 0
          ? colors.slice(0, 12).map((c) => (typeof c === "string" && /^#[0-9A-Fa-f]{6}$/.test(c) ? c : "#7B6B72"))
          : DEFAULT_CUSTOM_CHART_COLORS;
        set((state) => ({
          themeState: {
            ...state.themeState,
            customChartColors: safe,
            ...(state.themeState.chartPaletteId === "custom" ? { chartColors: safe } : {}),
          },
        }));
      },

      updateCustomStyles: (styles: Record<string, string>) => {
        const { preset, currentMode } = get().themeState;
        const themePreset = getPresetByName(preset);

        set((state) => ({
          themeState: {
            ...state.themeState,
            customStyles: { ...state.themeState.customStyles, ...styles },
          },
        }));

        if (themePreset) {
          applyThemeToDocument(themePreset, currentMode, get().themeState.customStyles);
        }
      },

      resetCustomStyles: () => {
        const { preset, currentMode } = get().themeState;
        const themePreset = getPresetByName(preset);

        set((state) => ({
          themeState: {
            ...state.themeState,
            customStyles: {},
          },
        }));

        if (themePreset) {
          applyThemeToDocument(themePreset, currentMode, {});
        }
      },

      getThemeStyles: () => {
        const { styles, currentMode, preset } = get().themeState;
        // Ensure styles are available, compute if missing
        if (!styles || !styles[currentMode]) {
          const presetObj = getPresetByName(preset);
          if (presetObj) {
            const computedStyles = getPresetThemeStyles(preset);
            // Update the store with computed styles
            set((state) => ({
              themeState: {
                ...state.themeState,
                styles: computedStyles,
              },
            }));
            return computedStyles[currentMode] || {};
          }
        }
        return styles[currentMode] || {};
      },

      getChartColors: () => {
        return get().themeState.chartColors || defaultChartColors;
      },
    }),
    {
      name: "editor-store",
      partialize: (state) => ({
        themeState: {
          preset: state.themeState.preset,
          currentMode: state.themeState.currentMode,
          customStyles: state.themeState.customStyles,
          chartPaletteId: state.themeState.chartPaletteId,
          customChartColors: state.themeState.customChartColors,
        },
      }),
      version: 3,
    }
  )
);
